import { Component, Injector, Input, OnInit } from '@angular/core';
import { take } from 'rxjs/operators';
import { SignatureAndLicenseService } from './signature-and-license.service';
import { WidgetDynamicBaseComponent } from '../../widgetDynamicBase';

/**
 * Signature Widget
 */
@Component({
  selector: 'signature-widget',
  templateUrl: './signature-widget.component.html',
  styleUrls: ['./signature-widget.component.scss', '../../common/css/general.scss']
})
export class SignatureWidgetComponent extends WidgetDynamicBaseComponent implements OnInit {

  /**
   * Widget Guid
   */
  @Input() widgetGuid: string;

  /**
   * Current layout
   */
  public currentLayoutIndex = 1;

  /**
  * Constructor
  * @param signatureAndLicenseService SignatureAndLicenseService
  */
  constructor(injector: Injector, public signatureAndLicenseService: SignatureAndLicenseService) {
    super(injector, 'signatureAndLicense', false);
  }

  /**
   * Initializes the component
   */
  ngOnInit() {
    // will call registerEvents and onInitialized when config (this.widgetConfig and this.page) is ready
    this.executeWidgetDataLoadedInWidgetClass = true;

    this.initWidgetConfig();
    this.widgetResized();
  }

  /**
   * Called by base class to let widget register to various app events (like saving, loading, resizing etc)
   */
  registerEvents(): void {
    this.useDefaultServiceSaveChangesHandler('signatureAndLicense', this.provideSaveObject.bind(this));
  }

  /**
   * Choose next available layout when adding text widget into report document
   */
  private chooseNextLayout() {
    const activeDocument = this.rdStorage.getActiveDocument();
    const usedLayouts: string[] = [];
    for (const page of activeDocument.data.pages) {
      for (const widget of page.widgets) {
        if (widget.widgetId === this.widgetConfig.widgetId) {//just text widgets
          if (usedLayouts.indexOf(widget.widgetSettings.currentLayout) < 0) {
            usedLayouts.push(widget.widgetSettings.currentLayout);
          }
        }
      }
    }

    if (!this.widgetConfig.widgetSettings.availableLayouts) {
      this.widgetConfig.widgetSettings.availableLayouts = ["1", "2", "3", "4"];
    }
    for (const layout of this.widgetConfig.widgetSettings.availableLayouts) {
      if (usedLayouts.indexOf(layout) < 0) {
        this.currentLayout = layout;
        this.widgetConfig.widgetSettings.currentLayout = this.currentLayout;
        break;
      }
    }

    setTimeout(() => {
      this.eventBus.broadcastEvent('widgetsChanged');
    });
  }

  /**
   * Layout change handler
   * @param newLayout
   */
  public layoutChanged(newLayout: string): void {
    super.layoutChanged(newLayout);

    this.currentLayoutIndex = parseInt(newLayout) - 1;
  }

  /**
   * Called by base class to inform widget that it is initialized
   * Mostly used to define where widget data should be loaded from
   */
  onInitialized(): void {
    this.mockupData$ = this.widgetService.loadMockup('signatureAndLicense').pipe(take(1));

    const reportId = this.mockup ? -1 : this.getReport();

    if (!this.currentLayout || this.currentLayout === "default" || this.currentLayout === "ChosenOnAdd") {
      this.chooseNextLayout();
    }

    this.signatureAndLicenseService.selectedData$.subscribe((data: any[]) => {
      if (data) {
        //setDirtyFlag was called even though nothing changed (e.g. loading report), so we need some double check
        const dataChanged = this.editMode || !this.widgetData || (JSON.stringify(this.widgetData) !== JSON.stringify(data));
        this.updateWidgetData(data);
        if (dataChanged) {
          this.setDirtyFlag(true, true);
        }
      }
    });

    this.currentLayoutIndex = parseInt(this.currentLayout) -1;

    this.signatureAndLicenseService.init(reportId);

    this.widgetData$ = this.mockup ?
      this.mockupData$
      : this.widgetService.load('signatureAndLicense', this.getReport()).pipe();

    this.signatureAndLicenseService.widgetDataLoaded$.subscribe(data => {
      if (data) {
        this.page.widgetDataLoaded(this.widgetConfig.widgetSettings.id);
        this.setDirtyFlag(false);
      }
    });
  }

  /**
   * Widget data is ready
   */
  protected widgetDataReady(): void {
    this.signatureAndLicenseService.setInitialData(this.widgetData);
  }

  /**
   * Called by base class in save process. Widget should return object to be saved
   */
  async provideSaveObject(): Promise<any> {
    return this.widgetData;
  }

  /**
   * Get selected licence number
   */
  public getLicenseNo(widgetData) {
    if (widgetData?.licenses?.length) {
      return widgetData.licenses[0].number;
    }
    return 'N/A';
  }

  public getMinimumRowIndex(): number {
    return 1;
  }

  public getMaximumRowIndex(): number {
    if (this.widgetData) {
      return this.widgetData?.rows?.length + 11;
    }
    return -1;
  }

  public moreColumnsRequestedIfOnPortraitPage(): boolean {
    throw new Error('Method not implemented.');
  }
}
